import { range } from "@formant/core/common/range";
import { serializeHash } from "@formant/core/common/serializeHash";

import { IPatternSegment } from "./IPatternSegment";
import { RoutePattern, RouteProps } from "./IRoute";
import { RoutePartPatternSegment } from "./RoutePartPatternSegment";
import { SimplePatternSegment } from "./SimplePatternSegment";

export const extractSegments = (p: string): string[] =>
    p.substring(1).split("/");

export function parseRoutePattern(pattern: RoutePattern) {
    if (Array.isArray(pattern)) {
        return pattern.map(it =>
            typeof it === "string"
                ? new SimplePatternSegment(it)
                : new RoutePartPatternSegment(it),
        );
    }

    return extractSegments(pattern).map(it => new SimplePatternSegment(it));
}

export function routePath<TProps extends RouteProps>(
    segments: IPatternSegment[],
    parameters?: TProps,
    hashValues?: { [key: string]: any },
): string {
    const params: { [key: string]: string } | undefined = parameters;
    const parts = segments.map(segment => {
        if (!segment.isVariable) {
            return segment.name;
        }

        const param = params?.[segment.name];
        if (!param) {
            throw new Error(
                `Cannot generate route! :${segment.name} is missing`,
            );
        }

        if (!segment.match(param)) {
            throw new Error(
                `Cannot generate route! :${segment.name} is malformed`,
            );
        }
        return params[segment.name];
    });
    const hash = hashValues ? `#${serializeHash(hashValues)}` : "";
    return `/${parts.join("/")}${hash}`;
}

export function extractParameters<TProps extends RouteProps>(
    segments: IPatternSegment[],
    pathWithHash: string,
): TProps | false {
    const [path] = pathWithHash.split("#");

    const pathSegments = extractSegments(path);

    if (segments.length !== pathSegments.length) {
        return false;
    }

    return range(0, segments.length).reduce<{ [key: string]: string } | false>(
        (result, i) => {
            if (result === false) {
                return false;
            }

            const patternSegment = segments[i];
            const pathSegment = pathSegments[i];

            if (patternSegment.isVariable) {
                return patternSegment.match(pathSegment)
                    ? { ...result, [patternSegment.name]: pathSegment }
                    : false;
            }

            return patternSegment.match(pathSegment) ? result : false;
        },
        {},
    ) as TProps | false;
}
